/* istanbul ignore file */

import React, { memo, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Platform, StyleSheet, View } from 'react-native';
import { type Edges, SafeAreaView } from 'react-native-safe-area-context';

import { theme } from '@garnish/constants';
import { BodyText, Button, HStack, Illus404, useResponsive } from '@garnish/core';
import { Image } from '@garnish/media';
import { Modal } from '@garnish/overlays';
import { useSelector } from '@xstate/react';

import { useLocationSearchHelpers } from '../../../../../../hooks/useLocationSearchHelpers';
import { type LocationSearchService } from '../../../../../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchDeliveryResultWithoutNearbyStore = memo(
  (props: LocationSearchDeliveryResultWithoutNearbyStoreProps) => {
    const { locationSearchActorRef } = props;

    const { match } = useResponsive();

    const ResponsiveComponent = match([
      LocationSearchDeliveryResultWithoutNearbyStoreXS,
      LocationSearchDeliveryResultWithoutNearbyStoreSM,
    ]);

    return <ResponsiveComponent locationSearchActorRef={locationSearchActorRef} />;
  },
);

// ─── Responsive Components ───────────────────────────────────────────────────

export const LocationSearchDeliveryResultWithoutNearbyStoreXS = memo(
  (props: LocationSearchDeliveryResultWithoutNearbyStoreProps) => {
    const { locationSearchActorRef } = props;

    const { formatMessage } = useIntl();

    const { searchDeliveryAddresses, setLocationSearchType } = useLocationSearchHelpers({
      locationSearchActorRef,
    });

    const deliveryLocationAddress = useSelector(
      locationSearchActorRef,
      (snapshot) => snapshot.context.deliveryLocation?.address,
    );

    // ─── Handlers ────────────────────────────────────────────────────────

    const handleTryNewAddressBtnPress = useCallback(() => {
      searchDeliveryAddresses('');
    }, [searchDeliveryAddresses]);

    const handleChangeToPickupSearchTypeBtnPress = useCallback(() => {
      setLocationSearchType('pickup');
    }, [setLocationSearchType]);

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View style={StyleSheet.absoluteFill}>
        <Modal
          visible
          accessibilityLabel={formatMessage(messages.deliveryUnavailableMobile)}
          size="small"
          animationType="fade"
          withSafeAreaView={false}
        >
          <SafeAreaView edges={SAFE_AREA_EDGES} style={styles.modalContent}>
            <Modal.Headline style={styles.modalHeading}>
              {formatMessage(messages.deliveryUnavailableMobile)}
            </Modal.Headline>

            <Modal.BodyText>{deliveryLocationAddress}</Modal.BodyText>

            <Modal.Footer withoutTopBorder>
              <HStack itemsPerRow={2} gap={theme.spacing['4']}>
                <Button size="large" palette="secondary" onPress={handleTryNewAddressBtnPress}>
                  {formatMessage(messages.deliveryResultClose)}
                </Button>

                <Button size="large" onPress={handleChangeToPickupSearchTypeBtnPress}>
                  {formatMessage(messages.deliveryOrderPickup)}
                </Button>
              </HStack>
            </Modal.Footer>
          </SafeAreaView>
        </Modal>
      </View>
    );
  },
);

export const LocationSearchDeliveryResultWithoutNearbyStoreSM = memo(
  (props: LocationSearchDeliveryResultWithoutNearbyStoreProps) => {
    const { locationSearchActorRef } = props;

    const { formatMessage } = useIntl();

    const { searchDeliveryAddresses, setLocationSearchType } = useLocationSearchHelpers({
      locationSearchActorRef,
    });

    const deliveryLocationAddress = useSelector(
      locationSearchActorRef,
      (snapshot) => snapshot.context.deliveryLocation?.address,
    );

    // ─── Handlers ────────────────────────────────────────────────────────

    const handleTryNewAddressBtnPress = useCallback(() => {
      searchDeliveryAddresses('');
    }, [searchDeliveryAddresses]);

    const handleChangeToPickupSearchTypeBtnPress = useCallback(() => {
      setLocationSearchType('pickup');
    }, [setLocationSearchType]);

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View style={styles.container}>
        <Image source={Illus404} style={styles.illustration} contentFit="contain" />

        <BodyText sizeMatch={['14', '16']} bold style={styles.address}>
          {deliveryLocationAddress}
        </BodyText>

        <BodyText sizeMatch={['14', '16']} style={styles.text}>
          {formatMessage(messages.deliveryUnavailable)}
        </BodyText>

        <HStack itemsPerRow={2} gap={theme.spacing['4']}>
          <Button
            size="large"
            palette="secondary"
            style={styles.cta}
            onPress={handleTryNewAddressBtnPress}
          >
            {formatMessage(messages.deliveryNewAddress)}
          </Button>

          <Button size="large" style={styles.cta} onPress={handleChangeToPickupSearchTypeBtnPress}>
            {formatMessage(messages.deliveryOrderPickup)}
          </Button>
        </HStack>
      </View>
    );
  },
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  deliveryUnavailableMobile: {
    defaultMessage: 'Ah snap peas, we’re not delivering to your address',
    description: 'Delivery | Unavailable Mobile',
  },
  deliveryResultClose: {
    defaultMessage: 'Close',
    description: 'Delivery Result | Close',
  },
  deliveryOrderPickup: {
    defaultMessage: 'Order pickup',
    description: 'Delivery | Order Pickup',
  },
  deliveryUnavailable: {
    defaultMessage:
      'Either delivery is not available at the moment or we haven’t launched in your area.',
    description: 'Delivery | Unavailable',
  },
  deliveryNewAddress: {
    defaultMessage: 'Try a new address',
    description: 'Delivery | New Address',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: theme.spacing['2'],
    paddingBottom: theme.spacing['6'],
  },
  illustration: {
    width: 200,
    height: 200,
  },
  address: {
    textAlign: 'center',
    marginVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['10'],
  },
  text: {
    textAlign: 'center',
    marginBottom: theme.spacing['6'],
    paddingHorizontal: theme.spacing['6'],
  },
  cta: {
    paddingHorizontal: theme.spacing['2'],
  },
  modalContent: {
    paddingTop: theme.spacing['6'],
    paddingBottom: Platform.select({
      web: 0,
      default: theme.spacing['6'],
    }),
    paddingHorizontal: theme.spacing['2'],
  },
  modalHeading: {
    marginBottom: theme.spacing['4'],
  },
  extraPadding: {
    paddingBottom: theme.spacing['4'],
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const SAFE_AREA_EDGES: Edges = ['bottom'];

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchDeliveryResultWithoutNearbyStoreProps = {
  locationSearchActorRef: LocationSearchService;
};
